import { record } from 'rrweb';
import { pack } from '@rrweb/packer';
import { createFlushWorker } from './flush-worker';
import type { FlushWorkerRequest, FlushWorkerResponse } from './flush-worker';
import type {
  RecorderConfig,
  SessionInfo,
  RecordedEvent,
  EventPayload,
  SessionPayload,
  StopRecordingFn,
  PartialRecorderConfig,
  DevToolsNetworkEvent,
  DevToolsConsoleLog,
  DevToolsPerformanceData,
  DevToolsResourceTiming,
  CapturedError,
  CapturedResourceError,
  CapturedResourceErrorType,
  FrustrationEvent,
  FrustrationData
} from './types';

/**
 * Encode a string to base64 (handles binary data from rrweb packer).
 *
 * Uses chunked String.fromCharCode.apply so we pay O(n/CHUNK) concatenations
 * instead of O(n²) from per-char += growth. FullSnapshot payloads are often
 * 500KB–2MB and the naive loop blocks the main thread for seconds.
 */
export function stringToBase64(str: string): string {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i);
  }
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
  }
  return btoa(binary);
}

/**
 * Captures network requests (fetch and XMLHttpRequest)
 */
/**
 * Scrub credential material from text the recorder captures. Real apps leak
 * tokens into observable surfaces constantly — SignalR appends
 * `?access_token=<JWT>` to its websocket URL and logs it to the console,
 * APIs carry keys in query strings — and without scrubbing, every one of
 * those ends up stored in the recording, readable by anyone with dashboard
 * access. Three passes:
 *   1. token-ish query params        → ?access_token=[REDACTED]
 *   2. auth-ish header/kv patterns   → Authorization: [REDACTED]
 *   3. bare JWTs anywhere            → [REDACTED_JWT]
 */
const TOKEN_QUERY_RE = /([?&#](?:access_token|refresh_token|id_token|token|api_key|apikey|auth|key|secret|password|pwd|sig|signature)=)[^&#\s"']+/gi;
const AUTH_HEADER_RE = /((?:authorization|x-api-key|api[-_]?key)["']?\s*[:=]\s*["']?(?:bearer\s+|basic\s+)?)[a-z0-9\-._~+/=]{8,}/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}\b/g;

export function redactSecrets(text: string): string {
  if (!text) return text;
  return text
    .replace(TOKEN_QUERY_RE, '$1[REDACTED]')
    .replace(AUTH_HEADER_RE, '$1[REDACTED]')
    .replace(JWT_RE, '[REDACTED_JWT]');
}

export class NetworkCaptor {
  private events: DevToolsNetworkEvent[] = [];
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;
  private idCounter = 0;
  private collectorUrl: string;

  constructor(collectorUrl: string) {
    this.collectorUrl = collectorUrl;
  }

  start(): void {
    this.interceptFetch();
    this.interceptXHR();
  }

  stop(): void {
    if (this.originalFetch) {
      (globalThis as unknown as Record<string, unknown>).fetch = this.originalFetch;
    }
    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
    }
    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
    }
  }

  getEvents(): DevToolsNetworkEvent[] { return [...this.events]; }
  clear(): void { this.events = []; }

  private isCollectorRequest(url: string): boolean {
    try {
      const urlObj = new URL(url, window.location.origin);
      const collectorObj = new URL(this.collectorUrl);
      return urlObj.origin === collectorObj.origin;
    } catch {
      return url.startsWith(this.collectorUrl);
    }
  }

  private interceptFetch(): void {
    if (typeof fetch === 'undefined') return;
    this.originalFetch = fetch.bind(globalThis);
    const self = this;
    (globalThis as unknown as Record<string, unknown>).fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      if (self.isCollectorRequest(url)) {
        return self.originalFetch!.call(globalThis, input, init);
      }

      const id = String(++self.idCounter);
      const startTime = Date.now();
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();

      // Redact ONLY the captured copy — the real request goes out untouched.
      const capturedUrl = redactSecrets(url);
      return self.originalFetch!.call(globalThis, input, init).then(
        (response) => {
          self.events.push({
            id, timestamp: startTime, method, url: capturedUrl,
            status: response.status,
            duration: Date.now() - startTime,
            size: parseInt(response.headers.get('content-length') || '0') || 0,
            type: 'fetch',
            traceId: response.headers.get('x-trace-id') || undefined
          });
          return response;
        },
        (error) => {
          self.events.push({
            id, timestamp: startTime, method, url: capturedUrl,
            status: 0, duration: Date.now() - startTime, size: 0, type: 'fetch'
          });
          throw error;
        }
      );
    };
  }

  private interceptXHR(): void {
    if (typeof XMLHttpRequest === 'undefined') return;
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    const self = this;
    const origOpen = this.originalXHROpen;
    const origSend = this.originalXHRSend;

    XMLHttpRequest.prototype.open = function(method: string, url: string, ...rest: unknown[]) {
      (this as unknown as Record<string, unknown>)._captureId = String(++self.idCounter);
      (this as unknown as Record<string, unknown>)._captureUrl = url;
      (this as unknown as Record<string, unknown>)._captureMethod = method.toUpperCase();
      (this as unknown as Record<string, unknown>)._captureStart = Date.now();
      (this as unknown as Record<string, unknown>)._captureIsCollector = self.isCollectorRequest(url);
      return origOpen!.apply(this, [method, url, ...rest] as Parameters<typeof XMLHttpRequest.prototype.open>);
    };

    XMLHttpRequest.prototype.send = function(body?: ArrayBuffer | Blob | FormData | string | null) {
      if (!(this as unknown as Record<string, unknown>)._captureIsCollector) {
        const id = (this as unknown as Record<string, unknown>)._captureId as string;
        const url = (this as unknown as Record<string, unknown>)._captureUrl as string;
        const method = (this as unknown as Record<string, unknown>)._captureMethod as string;
        const startTime = (this as unknown as Record<string, unknown>)._captureStart as number;

        this.addEventListener('loadend', function() {
          // Prefer the Content-Length header — reading responseText forces the
          // browser to materialise the full response body as a string.
          let size = 0;
          let traceId: string | undefined;
          try {
            const cl = this.getResponseHeader('content-length');
            if (cl) size = parseInt(cl, 10) || 0;
            traceId = this.getResponseHeader('x-trace-id') || undefined;
          } catch { /* ignore */ }
          self.events.push({
            id, timestamp: startTime, method, url: redactSecrets(url),
            status: this.status,
            duration: Date.now() - startTime, size, type: 'xhr', traceId
          });
        });
      }
      return origSend!.apply(this, [body]);
    };
  }
}

/**
 * Captures console output (log, info, warn, error, debug)
 */
export class ConsoleCaptor {
  private static readonly MAX_ARG_STRINGIFY_LEN = 2000;
  private static readonly MAX_MESSAGE_LEN = 8000;

  private logs: DevToolsConsoleLog[] = [];
  private originals: Record<string, (...args: unknown[]) => void> = {};
  private idCounter = 0;

  /**
   * Lightweight stringify that caps output size mid-serialization so we don't
   * pay a full JSON.stringify cost on large Redux/state dumps on the console hot path.
   */
  private static safeStringifyArg(a: unknown): string {
    if (typeof a === 'string') return a;
    if (a === null || a === undefined) return String(a);
    if (typeof a !== 'object') return String(a);
    try {
      let len = 0;
      const max = ConsoleCaptor.MAX_ARG_STRINGIFY_LEN;
      const seen = new WeakSet<object>();
      const s = JSON.stringify(a, (_key, value) => {
        if (len > max) return undefined;
        if (value && typeof value === 'object') {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        if (typeof value === 'string' && value.length > 500) {
          const t = value.substring(0, 500) + '…';
          len += t.length;
          return t;
        }
        len += 10;
        return value;
      });
      return s.length > max ? s.substring(0, max) + '…[truncated]' : s;
    } catch {
      return String(a);
    }
  }

  start(): void {
    const levels: ('log' | 'info' | 'warn' | 'error' | 'debug')[] = ['log', 'info', 'warn', 'error', 'debug'];
    for (const level of levels) {
      this.originals[level] = (console as unknown as Record<string, (...args: unknown[]) => void>)[level];
      const self = this;
      (console as unknown as Record<string, (...args: unknown[]) => void>)[level] = function(...args: unknown[]) {
        // Skip SessionRecorder's own logs
        if (args.length > 0 && args[0] === '[SessionRecorder]') {
          self.originals[level].apply(console, args);
          return;
        }
        let message = args.map(ConsoleCaptor.safeStringifyArg).join(' ');
        if (message.length > ConsoleCaptor.MAX_MESSAGE_LEN) {
          message = message.substring(0, ConsoleCaptor.MAX_MESSAGE_LEN) + '…[truncated]';
        }
        // Console output is a notorious token leak (SignalR logs its
        // websocket URL incl. ?access_token=<JWT>) — scrub before storing.
        message = redactSecrets(message);
        self.logs.push({
          id: String(++self.idCounter),
          timestamp: Date.now(),
          level,
          message
        });
        self.originals[level].apply(console, args);
      };
    }
  }

  stop(): void {
    for (const [level, fn] of Object.entries(this.originals)) {
      (console as unknown as Record<string, (...args: unknown[]) => void>)[level] = fn;
    }
  }

  getLogs(): DevToolsConsoleLog[] { return [...this.logs]; }
  clear(): void { this.logs = []; }
}

/**
 * Captures performance metrics (resource timings, web vitals)
 */
export class PerformanceCaptor {
  private data: DevToolsPerformanceData = { metrics: [], webVitals: {}, resources: [] };
  private observer: PerformanceObserver | null = null;
  private webVitalObservers: PerformanceObserver[] = [];
  private clsValue = 0;
  private inpValue = 0;
  private tbtValue = 0;
  private longTaskCount = 0;

  start(): void {
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const re = entry as PerformanceResourceTiming;
            this.data.resources!.push({
              name: re.name,
              initiatorType: re.initiatorType || 'other',
              startTime: re.startTime,
              duration: re.duration,
              transferSize: re.transferSize || 0,
              decodedBodySize: re.decodedBodySize || 0
            } as DevToolsResourceTiming);
          }
        }
      });
      this.observer.observe({ entryTypes: ['resource'] });
    } catch { /* PerformanceObserver not fully supported */ }

    this.captureWebVitals();
  }

  private captureWebVitals(): void {
    // LCP: the entry's startTime is the render/load time of the largest element.
    this.observeType({ entryTypes: ['largest-contentful-paint'] }, (entry) => {
      this.data.webVitals!.lcp = Math.round(entry.startTime);
    });

    // CLS: cumulative sum of layout-shift scores, skipping shifts right after input.
    this.observeType({ entryTypes: ['layout-shift'] }, (entry) => {
      const ls = entry as unknown as { value: number; hadRecentInput: boolean };
      if (!ls.hadRecentInput) {
        this.clsValue += ls.value;
        this.data.webVitals!.cls = Math.round(this.clsValue * 1000) / 1000;
      }
    });

    // FID: input delay = processingStart - startTime (not the event duration).
    this.observeType({ entryTypes: ['first-input'] }, (entry) => {
      const fe = entry as PerformanceEventTiming;
      this.data.webVitals!.fid = Math.round(fe.processingStart - fe.startTime);
    });

    // Long tasks (>50ms main-thread blocks): count + Total Blocking Time.
    this.observeType({ entryTypes: ['longtask'] }, (entry) => {
      this.longTaskCount++;
      this.tbtValue += Math.max(0, entry.duration - 50);
    });

    // INP approximation: the largest interaction latency this session.
    this.observeType({ type: 'event', durationThreshold: 40, buffered: true } as PerformanceObserverInit, (entry) => {
      const e = entry as PerformanceEventTiming;
      if (((e as unknown as { interactionId?: number }).interactionId ?? 0) > 0) {
        this.inpValue = Math.max(this.inpValue, e.duration);
      }
    });

    // FCP: first-contentful-paint from the 'paint' timeline.
    this.observeType({ entryTypes: ['paint'] }, (entry) => {
      if (entry.name === 'first-contentful-paint') {
        this.data.webVitals!.fcp = Math.round(entry.startTime);
      }
    });
  }

  private observeType(init: PerformanceObserverInit, onEntry: (entry: PerformanceEntry) => void): void {
    try {
      const obs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) onEntry(entry);
      });
      obs.observe(init);
      this.webVitalObservers.push(obs);
    } catch { /* entry type not supported in this browser */ }
  }

  stop(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    for (const obs of this.webVitalObservers) {
      obs.disconnect();
    }
  }

  getData(): DevToolsPerformanceData {
    const webVitals = { ...this.data.webVitals };
    if (this.inpValue > 0) webVitals.inp = Math.round(this.inpValue);
    if (this.tbtValue > 0) webVitals.tbt = Math.round(this.tbtValue);
    if (this.longTaskCount > 0) webVitals.longTaskCount = this.longTaskCount;
    const nav = this.getNavigationTiming();
    if (nav) {
      webVitals.ttfb = nav.ttfb;
      webVitals.domContentLoaded = nav.domContentLoaded;
      webVitals.loadComplete = nav.loadComplete;
    }
    return {
      metrics: [...(this.data.metrics || [])],
      webVitals,
      resources: [...(this.data.resources || [])]
    };
  }

  private getNavigationTiming(): { ttfb: number; domContentLoaded: number; loadComplete: number } | null {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (!nav) return null;
      return {
        ttfb: Math.round(nav.responseStart),
        domContentLoaded: Math.round(nav.domContentLoadedEventEnd),
        loadComplete: Math.round(nav.loadEventEnd)
      };
    } catch { return null; }
  }
}

/**
 * Captures uncaught JavaScript errors and unhandled promise rejections
 */
export class ErrorCaptor {
  private errors: CapturedError[] = [];
  private idCounter = 0;
  private originalOnError: OnErrorEventHandler = null;
  private originalOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;

  start(): void {
    this.originalOnError = window.onerror;
    this.originalOnUnhandledRejection = window.onunhandledrejection as
      ((event: PromiseRejectionEvent) => void) | null;

    window.onerror = (message, source, lineno, colno, error) => {
      this.errors.push({
        id: String(++this.idCounter),
        timestamp: Date.now(),
        type: 'uncaught',
        message: String(message),
        stack: error?.stack,
        source: source ?? undefined,
        lineno: lineno ?? undefined,
        colno: colno ?? undefined,
        errorName: error?.name
      });
      if (this.originalOnError) {
        return (this.originalOnError as Function).call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.errors.push({
        id: String(++this.idCounter),
        timestamp: Date.now(),
        type: 'unhandledrejection',
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
        errorName: reason instanceof Error ? reason.name : undefined
      });
      if (this.originalOnUnhandledRejection) {
        this.originalOnUnhandledRejection(event);
      }
    };
  }

  stop(): void {
    window.onerror = this.originalOnError;
    window.onunhandledrejection = this.originalOnUnhandledRejection as typeof window.onunhandledrejection;
  }

  getErrors(): CapturedError[] { return [...this.errors]; }
  clear(): void { this.errors = []; }

  /**
   * Manually capture an error (for explicit error reporting)
   */
  capture(error: Error): void {
    this.errors.push({
      id: String(++this.idCounter),
      timestamp: Date.now(),
      type: 'uncaught',
      message: error.message,
      stack: error.stack,
      errorName: error.name
    });
  }
}

/**
 * Captures failures on resource-tag loads (<img>, <script>, <link>) that
 * bypass fetch/XHR. NetworkCaptor doesn't see those — they're loaded by
 * the browser parser, not JS. Without this, an <img src="/api/x"> 404 is
 * invisible to the entire pipeline.
 *
 * Approach: listen to the capture-phase 'error' event on window. Bubbling
 * doesn't fire for resource errors, so we must use capture: true.
 * PerformanceObserver is intentionally NOT used here — for failed resource
 * loads it gives us 'transferSize: 0' but no actual HTTP status (browsers
 * never expose status for tag-level loads to JS for security reasons), so
 * it doesn't add information beyond what the error event already gives us.
 */
export class ResourceErrorCaptor {
  private errors: CapturedResourceError[] = [];
  private idCounter = 0;
  private handler: ((e: Event) => void) | null = null;

  start(): void {
    if (typeof window === 'undefined') return;
    this.handler = (e: Event) => {
      const target = e.target as Element | null;
      if (!target || target === (window as unknown as EventTarget)) return;
      const tagName = (target.tagName ?? '').toLowerCase();

      let type: CapturedResourceErrorType | null = null;
      let url = '';
      if (tagName === 'img') {
        type = 'image';
        url = (target as HTMLImageElement).src ?? '';
      } else if (tagName === 'script') {
        type = 'script';
        url = (target as HTMLScriptElement).src ?? '';
      } else if (tagName === 'link') {
        type = 'link';
        url = (target as HTMLLinkElement).href ?? '';
      }
      if (!type || !url) return;

      this.errors.push({
        id: String(++this.idCounter),
        timestamp: Date.now(),
        type,
        url,
        message: `${type} failed to load: ${url}`,
      });
    };
    window.addEventListener('error', this.handler, true);
  }

  stop(): void {
    if (this.handler && typeof window !== 'undefined') {
      window.removeEventListener('error', this.handler, true);
    }
    this.handler = null;
  }

  getErrors(): CapturedResourceError[] { return [...this.errors]; }
  clear(): void { this.errors = []; }
}

/**
 * Detects user frustration signals: rage clicks and dead clicks
 *
 * Rage click: 3+ clicks within 1 second in a 30px radius
 * Dead click: A click that produces no DOM mutation within 1 second
 */
export class FrustrationCaptor {
  private events: FrustrationEvent[] = [];
  private idCounter = 0;
  private clickBuffer: Array<{ timestamp: number; x: number; y: number }> = [];
  private pendingDeadClicks: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private mutationObserver: MutationObserver | null = null;
  private observing = false;
  private lastRageTimestamp = 0;
  private clickListener: ((e: MouseEvent) => void) | null = null;

  start(): void {
    // Dead-click detection watches for a DOM reaction to a click. The observer
    // is attached ONLY while a click is pending (a <=1s window) and detached
    // otherwise, so it never observes the whole body continuously on busy apps.
    this.mutationObserver = new MutationObserver(() => {
      for (const [, timer] of this.pendingDeadClicks) {
        clearTimeout(timer);
      }
      this.pendingDeadClicks.clear();
      this.stopObserving();
    });

    this.clickListener = (e: MouseEvent) => {
      const now = Date.now();
      const x = e.clientX;
      const y = e.clientY;
      const selector = this.getSelector(e.target as Element);

      // --- Rage click detection ---
      this.clickBuffer.push({ timestamp: now, x, y });
      // Purge clicks older than 1 second
      this.clickBuffer = this.clickBuffer.filter(c => now - c.timestamp < 1000);

      if (this.clickBuffer.length >= 3 && now - this.lastRageTimestamp > 500) {
        // Check if all buffered clicks are within 30px radius of current click
        const nearby = this.clickBuffer.filter(c =>
          Math.sqrt((c.x - x) ** 2 + (c.y - y) ** 2) < 30
        );
        if (nearby.length >= 3) {
          this.lastRageTimestamp = now;
          this.events.push({
            id: String(++this.idCounter),
            timestamp: now,
            type: 'rage-click',
            x,
            y,
            targetSelector: selector,
            clickCount: nearby.length
          });
        }
      }

      // --- Dead click detection ---
      const deadClickId = String(++this.idCounter);
      const timer = setTimeout(() => {
        this.pendingDeadClicks.delete(deadClickId);
        this.events.push({
          id: deadClickId,
          timestamp: now,
          type: 'dead-click',
          x,
          y,
          targetSelector: selector
        });
        if (this.pendingDeadClicks.size === 0) this.stopObserving();
      }, 1000);
      this.pendingDeadClicks.set(deadClickId, timer);
      this.startObserving();
    };

    document.addEventListener('click', this.clickListener, true);
  }

  // Structural changes only; attributes/characterData would flood on data-bound
  // apps and aren't meaningful as a click "response".
  private startObserving(): void {
    if (this.observing || !this.mutationObserver) return;
    this.mutationObserver.observe(document.body, { subtree: true, childList: true });
    this.observing = true;
  }

  private stopObserving(): void {
    if (!this.observing || !this.mutationObserver) return;
    this.mutationObserver.disconnect();
    this.observing = false;
  }

  stop(): void {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener, true);
      this.clickListener = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
      this.observing = false;
    }
    for (const timer of this.pendingDeadClicks.values()) {
      clearTimeout(timer);
    }
    this.pendingDeadClicks.clear();
  }

  getEvents(): FrustrationData {
    const rageClickCount = this.events.filter(e => e.type === 'rage-click').length;
    const deadClickCount = this.events.filter(e => e.type === 'dead-click').length;
    return {
      events: [...this.events],
      rageClickCount,
      deadClickCount,
      frustrationScore: Math.min(100, rageClickCount * 15 + deadClickCount * 8)
    };
  }

  clear(): void { this.events = []; }

  private getSelector(el: Element | null): string {
    if (!el) return '';
    try {
      if (el.id) return `#${el.id}`;
      const tag = el.tagName?.toLowerCase() || '';
      const cls = el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
      return tag + cls;
    } catch {
      return '';
    }
  }
}

const EVENT_TYPE_NAMES: Record<number, string> = {
  0: 'DomContentLoaded',
  1: 'Load',
  2: 'FullSnapshot',
  3: 'IncrementalSnapshot',
  4: 'Meta',
  5: 'Custom',
  6: 'Plugin'
};

// Cap on events awaiting a successful flush. Sized to survive a multi-minute
// collector outage (e.g. a redeploy) without loss at typical event rates. On
// overflow we drop mousemove/scroll first and never DOM mutations or snapshots
// (see requeueWithinBudget), so replay stays structurally intact.
const MAX_BUFFER_SIZE = 5000;

const DEFAULT_CONFIG: RecorderConfig = {
  collectorUrl: 'http://localhost:5000',
  batchSize: 10,
  flushInterval: 5000,
  sampling: {
    mousemove: 500,
    mouseInteraction: true,
    scroll: 500,
    input: 'last'
  },
  maskAllInputs: false,
  maskInputOptions: {
    password: true
  },
  debug: false
};

interface BufferedEvent {
  event: RecordedEvent;
  capturedAt: number;
  /** rrweb event type, captured before packing (which turns the event into an
   *  opaque string) so overflow pruning can tell mutations from mousemove. */
  type?: number;
  /** For type 3 (IncrementalSnapshot), the data.source discriminator
   *  (0=Mutation, 1=MouseMove, 2=MouseInteraction, 3=Scroll, 5=Input). */
  source?: number;
}

/**
 * Session Recorder class for recording user sessions with rrweb
 * @internal
 */
class SessionRecorderImpl {
  private sessionId: string | null = null;
  private events: BufferedEvent[] = [];
  private stopRecording: StopRecordingFn = null;
  private config: RecorderConfig = { ...DEFAULT_CONFIG };
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private startTime: number = 0;
  private initialized: boolean = false;
  private flushing: boolean = false;
  private sessionReady: Promise<void> = Promise.resolve();
  // Guards the one-shot page-reload marker so a recording restart within the
  // same page load can't emit a spurious second "reload" tick.
  private navigationMarkerEmitted: boolean = false;

  // Exponential backoff on flush failures. When the collector returns
  // 5xx or the fetch throws, doubling the wait each attempt (capped)
  // avoids hammering a struggling backend. Reset to zero on success.
  // Reads: flushEvents' entry check. Writes: success + failure branches
  // in main-thread flush + handleFlushResult.
  private nextAllowedFlushMs: number = 0;
  private currentBackoffMs: number = 0;
  private static readonly INITIAL_BACKOFF_MS = 1000;
  private static readonly MAX_BACKOFF_MS = 60_000;

  // Set by the packFn wrapper for the event currently being packed, then read
  // by emit() to tag the BufferedEvent — packing strips this info from the wire form.
  private pendingEventType?: number;
  private pendingEventSource?: number;

  // DevTools capture
  private networkCaptor: NetworkCaptor | null = null;
  private consoleCaptor: ConsoleCaptor | null = null;
  private performanceCaptor: PerformanceCaptor | null = null;
  private errorCaptor: ErrorCaptor | null = null;
  private resourceErrorCaptor: ResourceErrorCaptor | null = null;
  private frustrationCaptor: FrustrationCaptor | null = null;

  // Web Worker for offloading flush
  private flushWorker: Worker | null = null;
  private flushRequestId: number = 0;
  private pendingFlushEvents: Map<number, BufferedEvent[]> = new Map();

  /**
   * Initialize the session recorder
   */
  public init(options: PartialRecorderConfig = {}): void {
    if (this.initialized) {
      this.log('warn', 'SessionRecorder already initialized');
      return;
    }

    // Merge user options with defaults (deep merge for nested objects)
    this.config = {
      ...DEFAULT_CONFIG,
      ...options,
      sampling: { ...DEFAULT_CONFIG.sampling, ...(options.sampling || {}) },
      maskInputOptions: { ...DEFAULT_CONFIG.maskInputOptions, ...(options.maskInputOptions || {}) },
    };

    // Generate or use provided session ID
    this.sessionId = this.resolveSessionId();
    this.startTime = Date.now();

    // Start error capture before recording so init errors are caught
    this.errorCaptor = new ErrorCaptor();
    this.errorCaptor.start();
    this.resourceErrorCaptor = new ResourceErrorCaptor();
    this.resourceErrorCaptor.start();

    // Initialize flush worker
    this.flushWorker = createFlushWorker();
    if (this.flushWorker) {
      this.flushWorker.onmessage = (e: MessageEvent<FlushWorkerResponse>) => {
        this.handleFlushResult(e.data);
      };
      this.log('info', 'Flush worker created — serialization offloaded from main thread');
    } else {
      this.log('warn', 'Web Worker not available — flush will run on main thread');
    }

    // Start recording. When the browser exposes document.fonts.ready,
    // defer the actual rrweb start until fonts have loaded so the initial
    // FullSnapshot includes them. Without this, sessions started before
    // the browser cache is warm capture the page WITHOUT icon fonts —
    // same site, same code, different replay.
    //
    // Cap the wait at 2 seconds so a slow font CDN can't block recording
    // forever on a flaky network. Environments without the FontFaceSet API
    // (older browsers, jsdom in tests) just start synchronously.
    const fontsReady = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts?.ready;
    if (fontsReady) {
      this.waitForFonts(2000).then(() => this.scheduleStartRecording());
    } else {
      this.scheduleStartRecording();
    }

    // Start DevTools captures
    this.networkCaptor = new NetworkCaptor(this.config.collectorUrl);
    this.networkCaptor.start();
    this.consoleCaptor = new ConsoleCaptor();
    this.consoleCaptor.start();
    this.performanceCaptor = new PerformanceCaptor();
    this.performanceCaptor.start();
    this.frustrationCaptor = new FrustrationCaptor();
    this.frustrationCaptor.start();

    // Create session on backend; flushEvents awaits this to avoid a race where
    // rrweb emits a FullSnapshot and we POST /events before /session/start lands.
    this.sessionReady = this.createSession();

    // Set up periodic flush (flush when rrweb events OR devtools data is buffered)
    this.flushTimer = setInterval(() => {
      const hasRrwebEvents = this.events.length > 0;
      const hasDevToolsData = (this.networkCaptor?.getEvents().length || 0) > 0 ||
        (this.consoleCaptor?.getLogs().length || 0) > 0 ||
        (this.errorCaptor?.getErrors().length || 0) > 0 ||
        (this.resourceErrorCaptor?.getErrors().length || 0) > 0;
      if (hasRrwebEvents || hasDevToolsData) {
        this.flushEvents();
      }
    }, this.config.flushInterval);

    // Flush remaining events when the page is about to be unloaded. Two
    // signals because neither fires alone reliably across all platforms:
    //  - visibilitychange: fires when the tab is backgrounded (mobile primary)
    //  - pagehide:         fires on real tab/window close (modern desktop)
    // sendFinalFlush is idempotent (clears the buffer before posting) so
    // overlapping fires from multiple events never double-send.
    // Deliberately NO unload/beforeunload: Chrome's permissions policy can
    // block `unload` outright (the flush silently never fires), and a
    // beforeunload listener disqualifies the page from the back/forward
    // cache. pagehide covers every browser we support.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePagehide);

    this.initialized = true;
    this.log('info', 'SessionRecorder initialized with session ID:', this.sessionId);
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  /** sessionStorage key for the per-tab session record. */
  private static readonly SESSION_STORAGE_KEY = 'tracium_session_id';
  /** Continue the stored session only if the tab was active within this window. */
  private static readonly SESSION_IDLE_TIMEOUT_MS = 30 * 60_000; // 30 min
  /** Rotate regardless of activity once a session gets this old. */
  private static readonly SESSION_MAX_AGE_MS = 24 * 3_600_000; // 24 h
  /** Throttle for lastActivityAt writes — storage isn't free on the hot path. */
  private static readonly SESSION_TOUCH_INTERVAL_MS = 30_000;

  private sessionStartedAt = Date.now();
  private lastSessionTouch = 0;

  /**
   * Resolve the session id for this page load, giving F5 / Ctrl-Shift-R
   * continuity WITHIN THE TAB — but not forever:
   *   1. An explicit config.sessionId always wins (host app owns the id).
   *   2. Otherwise reuse the record stashed in sessionStorage
   *      ({id, startedAt, lastActivityAt}) — it survives a reload but is
   *      isolated per tab. The stored session is continued ONLY if the tab
   *      was active within SESSION_IDLE_TIMEOUT_MS AND the session is
   *      younger than SESSION_MAX_AGE_MS. Otherwise a fresh id is minted:
   *      a tab parked overnight becomes two sessions (one per work period)
   *      instead of one 30-hour blob, and an always-active kiosk tab still
   *      rotates daily. 30 min idle is the same convention GA/Amplitude/
   *      FullStory use for "a session".
   *   3. Legacy bare-string values (pre-rotation SDK builds) adopt the id
   *      with fresh timestamps.
   * Falls back to a per-load id when storage is unavailable (private mode,
   * sandboxed iframe) — same behaviour as before this change.
   */
  private resolveSessionId(): string {
    if (this.config.sessionId) return this.config.sessionId;
    const now = Date.now();
    try {
      const raw = window.sessionStorage.getItem(SessionRecorderImpl.SESSION_STORAGE_KEY);
      if (raw) {
        let id: string | null = null;
        let startedAt = now;
        let lastActivityAt = now;
        if (raw.startsWith('{')) {
          try {
            const rec = JSON.parse(raw) as { id?: string; startedAt?: number; lastActivityAt?: number };
            id = rec.id ?? null;
            startedAt = rec.startedAt ?? now;
            lastActivityAt = rec.lastActivityAt ?? now;
          } catch { id = null; }
        } else {
          // Legacy bare id written by an older SDK build — adopt it.
          id = raw;
          startedAt = now;
          lastActivityAt = now;
        }
        const withinIdle = now - lastActivityAt <= SessionRecorderImpl.SESSION_IDLE_TIMEOUT_MS;
        const withinAge = now - startedAt <= SessionRecorderImpl.SESSION_MAX_AGE_MS;
        if (id && withinIdle && withinAge) {
          this.sessionStartedAt = startedAt;
          this.writeSessionRecord(id, startedAt, now);
          return id;
        }
      }
      const fresh = this.generateSessionId();
      this.sessionStartedAt = now;
      this.writeSessionRecord(fresh, now, now);
      return fresh;
    } catch {
      return this.generateSessionId();
    }
  }

  private writeSessionRecord(id: string, startedAt: number, lastActivityAt: number): void {
    try {
      window.sessionStorage.setItem(
        SessionRecorderImpl.SESSION_STORAGE_KEY,
        JSON.stringify({ id, startedAt, lastActivityAt })
      );
    } catch {
      /* storage unavailable — reload will start a new session, non-fatal */
    }
  }

  /**
   * Refresh the stored lastActivityAt so an active tab keeps its session
   * across reloads. Called from the flush paths (a flush ⇒ there was
   * activity), throttled to one storage write per SESSION_TOUCH_INTERVAL_MS.
   */
  private touchSession(): void {
    // Host-app-owned ids (config.sessionId) never enter tab continuity —
    // the host controls the lifecycle, and persisting its id would leak it
    // into later page loads that don't pass one.
    if (this.config.sessionId) return;
    const id = this.sessionId;
    if (!id) return;
    const now = Date.now();
    if (now - this.lastSessionTouch < SessionRecorderImpl.SESSION_TOUCH_INTERVAL_MS) return;
    this.lastSessionTouch = now;
    this.writeSessionRecord(id, this.sessionStartedAt, now);
  }

  /** Persist the current session id so the next reload in this tab continues it. */
  private persistSessionId(id: string): void {
    this.sessionStartedAt = Date.now();
    this.writeSessionRecord(id, this.sessionStartedAt, this.sessionStartedAt);
  }

  /**
   * Resolve when the page's web fonts have finished loading (or after the
   * timeout, whichever comes first). Browsers that don't expose the
   * FontFaceSet ready promise just resolve immediately.
   */
  private waitForFonts(timeoutMs: number): Promise<void> {
    return new Promise<void>(resolve => {
      const settle = () => resolve();
      const timer = setTimeout(settle, timeoutMs);
      const fonts = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts;
      if (fonts?.ready) {
        fonts.ready.then(() => {
          clearTimeout(timer);
          settle();
        }).catch(() => settle());
      } else {
        clearTimeout(timer);
        settle();
      }
    });
  }

  /**
   * Start rrweb recording
   */
  // Defer the first (synchronous, DOM-serializing) rrweb snapshot to an idle
  // moment so it doesn't block the main thread during page load or user input.
  private scheduleStartRecording(): void {
    const ric = (globalThis as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => this.startRecording(), { timeout: 2000 });
    } else {
      setTimeout(() => this.startRecording(), 0);
    }
  }

  private startRecording(): void {
    const stopFn = record({
      emit: (event: RecordedEvent) => {
        this.events.push({
          event,
          capturedAt: Date.now(),
          type: this.pendingEventType,
          source: this.pendingEventSource
        });
        this.pendingEventType = undefined;
        this.pendingEventSource = undefined;
        // Flush when batch size is reached
        if (this.events.length >= this.config.batchSize) {
          this.flushEvents();
        }
      },
      // Capture the event's type/source before pack() turns it into an opaque
      // string, so overflow pruning can keep mutations and drop mousemove/scroll.
      packFn: (event: any) => {
        this.pendingEventType = event?.type;
        this.pendingEventSource = event?.type === 3 ? event?.data?.source : undefined;
        return pack(event);
      },
      sampling: this.config.sampling as any,
      maskAllInputs: this.config.maskAllInputs,
      maskInputOptions: this.config.maskInputOptions,
      collectFonts: true,
      inlineStylesheet: true,
      // Inline <img src> and CSS background-image url() refs as base64 data
      // URIs at snapshot time. Without this, icons referenced via
      // `background-image: url(...)` show as empty squares in replay because
      // the customer's CDN/origin blocks or no-longer serves the asset.
      // Cost: larger snapshots when the page has many images — fine for
      // icon-heavy UIs since icon files are small (~kB each).
      inlineImages: true
    });
    this.stopRecording = stopFn || null;
    this.log('info', 'rrweb recording started with compression');

    // Recording is now active — emit the reload marker if this page load was a
    // reload. Doing it here (not in init) guarantees addCustomEvent has a live
    // recorder to attach to.
    this.emitNavigationMarker();
  }

  /**
   * When the current page load was a browser reload (F5 / Ctrl-Shift-R), drop a
   * custom "page-reload" event so the replay progress bar shows a distinct tick
   * at the reload point. Without it, a reload inside a continued session looks
   * like the screen mysteriously resetting to a fresh snapshot.
   *
   * Uses the Navigation Timing API (PerformanceNavigationTiming.type). Note it
   * reports 'reload' for BOTH soft (F5) and hard (Ctrl-Shift-R) reloads — the
   * platform doesn't distinguish them — so we can mark "a reload happened", not
   * which kind. 'back_forward' (bfcache / back button) is also surfaced since it
   * likewise resets the view mid-session. One-shot per page load.
   */
  private emitNavigationMarker(): void {
    if (this.navigationMarkerEmitted) return;
    this.navigationMarkerEmitted = true;
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      const navType = nav?.type;
      if (navType === 'reload') {
        this.addCustomEvent('page-reload', { navigationType: navType }, { color: '#3b82f6' });
        this.log('info', 'Marked page reload on timeline');
      } else if (navType === 'back_forward') {
        this.addCustomEvent('page-restore', { navigationType: navType }, { color: '#8b5cf6' });
        this.log('info', 'Marked back/forward restore on timeline');
      }
    } catch {
      /* Navigation Timing unavailable (old browser / jsdom) — no marker, non-fatal. */
    }
  }

  /**
   * Create session on the backend
   */
  /**
   * Strip the query string and hash from a URL so we don't leak tokens,
   * session IDs, or other sensitive query params into recorded sessions.
   * Returns the original string if it can't be parsed.
   */
  private stripUrlParams(href: string): string {
    try {
      const u = new URL(href);
      return u.origin + u.pathname;
    } catch {
      return href.split('?')[0].split('#')[0];
    }
  }

  private async createSession(): Promise<void> {
    if (!this.sessionId) return;

    const payload: SessionPayload = {
      sessionId: this.sessionId,
      pageUrl: this.stripUrlParams(window.location.href),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      language: navigator.language,
      referrer: document.referrer || 'direct',
      ...(this.config.userId ? { userId: this.config.userId } : {})
    };

    try {
      const response = await fetch(`${this.config.collectorUrl}/api/session/start`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        this.log('info', 'Session created successfully');
      } else {
        this.log('error', 'Failed to create session:', response.status);
      }
    } catch (error) {
      this.log('error', 'Error creating session:', error);
    }
  }

  /**
   * Encode packed event to base64 to preserve binary data during JSON serialization
   */
  private encodePackedEvent(event: RecordedEvent): string {
    if (typeof event === 'string') {
      return stringToBase64(event);
    }
    return JSON.stringify(event);
  }

  /**
   * Map buffered events to the payload format
   */
  private mapEventsToPayload(events: BufferedEvent[]): EventPayload['events'] {
    return events.map(({ event, capturedAt }) => {
      const isPacked = typeof event === 'string';
      return {
        eventType: isPacked ? 'Packed' : this.getEventTypeName(event.type),
        eventData: this.encodePackedEvent(event),
        timestampMs: isPacked ? capturedAt : event.timestamp
      };
    });
  }

  /**
   * Collect current devtools data snapshot
   */
  /**
   * Gzip + base64 a JSON string using the browser-native CompressionStream.
   * Returns null on browsers that don't expose CompressionStream so the caller
   * can fall back to sending the raw object.
   */
  private async gzipBase64(json: string): Promise<string | null> {
    const Ctor = (globalThis as { CompressionStream?: { new (format: string): GenericTransformStream } }).CompressionStream;
    if (!Ctor) return null;
    try {
      const stream = new Blob([json]).stream().pipeThrough(new Ctor('gzip') as ReadableWritablePair);
      const buf = await new Response(stream).arrayBuffer();
      const bytes = new Uint8Array(buf);
      // btoa needs binary string; chunk to avoid stack overflow for big payloads
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
      }
      return btoa(binary);
    } catch {
      return null;
    }
  }

  private collectDevToolsData() {
    const errors = this.errorCaptor?.getErrors() || [];
    const resourceErrors = this.resourceErrorCaptor?.getErrors() || [];
    return {
      network: this.networkCaptor?.getEvents() || [],
      console: this.consoleCaptor?.getLogs() || [],
      performance: this.performanceCaptor?.getData(),
      errors,
      resourceErrors,
      frustration: this.frustrationCaptor?.getEvents()
    };
  }

  /**
   * Clear devtools buffers after successful flush
   */
  private clearDevToolsBuffers(): void {
    this.networkCaptor?.clear();
    this.consoleCaptor?.clear();
    this.errorCaptor?.clear();
    this.resourceErrorCaptor?.clear();
    this.frustrationCaptor?.clear();
  }

  /**
   * Handle flush result from the web worker
   */
  private handleFlushResult(result: FlushWorkerResponse): void {
    const pendingEvents = this.pendingFlushEvents.get(result.id);
    this.pendingFlushEvents.delete(result.id);

    if (result.ok) {
      this.log('info', `Worker sent ${result.eventsCount} events`);
      this.resetFlushBackoff();
    } else if (result.status === 410) {
      // Session gone on server — reset and start a new one; drop buffered events
      this.log('warn', 'Session gone on server, resetting and creating a new one');
      this.sessionId = this.generateSessionId();
      this.persistSessionId(this.sessionId); // subsequent reloads continue the NEW session, not the gone one
      this.createSession();
      // Not counted as a flush 'failure' — the new session will start clean.
      this.resetFlushBackoff();
    } else {
      this.log('error', 'Worker flush failed:', result.error);
      if (pendingEvents) {
        this.requeueWithinBudget(pendingEvents);
      }
      this.applyFlushBackoff();
    }
    this.flushing = false;
  }

  /**
   * Re-queue events that failed to flush, keeping the buffer within
   * MAX_BUFFER_SIZE. `failed` events are older than what's currently buffered,
   * so they go in front to preserve chronological order. On overflow we drop
   * low-value events (mousemove/scroll) oldest-first; DOM mutations, snapshots,
   * inputs and custom events are always kept so replay stays structurally
   * intact. Only if shedding every low-value event still overflows do we drop
   * the oldest remaining events as a last resort.
   */
  private requeueWithinBudget(failed: BufferedEvent[]): void {
    const merged = [...failed, ...this.events];
    if (merged.length <= MAX_BUFFER_SIZE) {
      this.events = merged;
      return;
    }

    const isLowValue = (e: BufferedEvent): boolean =>
      e.type === 3 && (e.source === 1 || e.source === 3);

    let overflow = merged.length - MAX_BUFFER_SIZE;
    let droppedLowValue = 0;
    const kept: BufferedEvent[] = [];
    for (const e of merged) {
      if (overflow > 0 && isLowValue(e)) {
        overflow--;
        droppedLowValue++;
        continue;
      }
      kept.push(e);
    }

    let droppedCritical = 0;
    if (kept.length > MAX_BUFFER_SIZE) {
      droppedCritical = kept.length - MAX_BUFFER_SIZE;
      kept.splice(0, droppedCritical);
    }

    this.events = kept;
    this.log(
      'warn',
      `Buffer over ${MAX_BUFFER_SIZE}; dropped ${droppedLowValue} mousemove/scroll` +
        (droppedCritical ? ` and ${droppedCritical} older events (last resort)` : '')
    );
  }

  /**
   * Flush events and devtools data to the backend.
   * Uses web worker when available, falls back to main thread.
   */
  /** Reset backoff after a successful flush — next tick can run immediately. */
  private resetFlushBackoff(): void {
    this.currentBackoffMs = 0;
    this.nextAllowedFlushMs = 0;
  }

  /** Apply exponential backoff after a failed flush. Doubles the delay,
   * capped at MAX_BACKOFF_MS. Subsequent flush ticks will no-op until
   * nextAllowedFlushMs elapses — the buffered events aren't lost, they
   * wait in the SDK buffer for the next legit flush attempt. */
  private applyFlushBackoff(): void {
    this.currentBackoffMs = this.currentBackoffMs === 0
      ? SessionRecorderImpl.INITIAL_BACKOFF_MS
      : Math.min(this.currentBackoffMs * 2, SessionRecorderImpl.MAX_BACKOFF_MS);
    this.nextAllowedFlushMs = Date.now() + this.currentBackoffMs;
    this.log('warn', `Flush backing off ${this.currentBackoffMs}ms after failure`);
  }

  private async flushEvents(): Promise<void> {
    if (!this.sessionId || this.flushing) return;
    // A flush attempt means the tab is alive and recording — refresh the
    // session record's lastActivityAt so a reload continues this session.
    this.touchSession();
    // Backoff gate — after a failed flush we skip subsequent ticks
    // until the backoff window elapses. Events keep accumulating in
    // the buffer (bounded by MAX_BUFFER_SIZE + requeueWithinBudget),
    // and the next flush that clears this gate will pick them all up.
    if (this.nextAllowedFlushMs > 0 && Date.now() < this.nextAllowedFlushMs) return;
    this.flushing = true;

    try {
      // Ensure /session/start has landed before POSTing /events;
      // otherwise the backend returns 410 Gone for the unknown session.
      await this.sessionReady;

      const eventsToSend = [...this.events];
      this.events = [];

      const devtoolsData = this.collectDevToolsData();

      // Skip if nothing to send
      if (eventsToSend.length === 0 && (devtoolsData.network as unknown[]).length === 0 &&
          (devtoolsData.console as unknown[]).length === 0 && (devtoolsData.errors as unknown[]).length === 0) {
        this.flushing = false;
        return;
      }

      this.clearDevToolsBuffers();

      // Try to gzip devtools to save bandwidth — falls back to inline if
      // CompressionStream isn't available (older browsers).
      const devtoolsGz = await this.gzipBase64(JSON.stringify(devtoolsData));

      // Offload to worker if available
      if (this.flushWorker) {
        const id = ++this.flushRequestId;
        this.pendingFlushEvents.set(id, eventsToSend);
        const msg: FlushWorkerRequest = {
          type: 'flush',
          id,
          collectorUrl: this.config.collectorUrl,
          headers: this.getHeaders(),
          sessionId: this.sessionId,
          userId: this.config.userId,
          events: eventsToSend.map(e => ({ event: e.event, capturedAt: e.capturedAt })),
          devtools: devtoolsGz ? undefined : devtoolsData,
          devtoolsGz: devtoolsGz ?? undefined
        };
        this.flushWorker.postMessage(msg);
        // flushing flag cleared in handleFlushResult
        return;
      }

      // Main-thread fallback
      const payload: EventPayload = {
        sessionId: this.sessionId,
        ...(this.config.userId ? { userId: this.config.userId } : {}),
        events: this.mapEventsToPayload(eventsToSend),
        ...(devtoolsGz ? { devtoolsGz } : { devtools: devtoolsData })
      };

      try {
        const response = await fetch(`${this.config.collectorUrl}/api/events`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          this.log('info', `Sent ${eventsToSend.length} events + devtools data`);
          this.resetFlushBackoff();
        } else if (response.status === 410) {
          // Session no longer exists on the server — drop buffered events and start a new session
          this.log('warn', 'Session gone on server, resetting and creating a new one');
          this.sessionId = this.generateSessionId();
          this.persistSessionId(this.sessionId); // subsequent reloads continue the NEW session, not the gone one
          this.createSession();
          this.resetFlushBackoff();
        } else {
          this.requeueWithinBudget(eventsToSend);
          this.log('error', 'Failed to send events:', response.status);
          this.applyFlushBackoff();
        }
      } catch (error) {
        this.requeueWithinBudget(eventsToSend);
        this.log('error', 'Error sending events:', error);
        this.applyFlushBackoff();
      }
    } finally {
      if (!this.flushWorker) {
        this.flushing = false;
      }
    }
  }

  /**
   * Get event type name from numeric type
   */
  private getEventTypeName(type: number): string {
    return EVENT_TYPE_NAMES[type] || 'Unknown';
  }

  /**
   * Fired when the tab is hidden — primary signal on mobile (app switch,
   * tab switch, OS sleep) where pagehide isn't guaranteed to run before
   * the JS context is suspended.
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.sendFinalFlush();
    }
  };

  /**
   * Fired on real tab/window close on modern browsers. More reliable than
   * beforeunload in current Firefox + Safari.
   */
  private handlePagehide = (): void => {
    this.sendFinalFlush();
  };

  /**
   * Final flush of buffered events + devtools data. Uses fetch with
   * keepalive:true so the request survives page unload — keepalive
   * preserves custom headers (unlike sendBeacon, which can't send
   * X-API-Key) and continues in the background after the JS context
   * is torn down.
   *
   * Idempotency: the local buffer is cleared BEFORE the fetch is
   * issued, so overlapping fires from visibilitychange + pagehide
   * during the same teardown only post once — the second call sees
   * an empty buffer and no-ops.
   */
  private sendFinalFlush(): void {
    if (!this.sessionId) return;
    // The teardown flush is also the last chance to stamp activity — a
    // quick reopen within the idle window then continues this session.
    this.touchSession();

    const eventsToSend = this.events;
    const devtoolsData = this.collectDevToolsData();
    const hasRrwebEvents = eventsToSend.length > 0;
    const hasDevTools = (devtoolsData.network as unknown[]).length > 0 ||
      (devtoolsData.console as unknown[]).length > 0 ||
      (devtoolsData.errors as unknown[]).length > 0;
    if (!hasRrwebEvents && !hasDevTools) return;

    this.events = [];
    this.clearDevToolsBuffers();

    const payload: EventPayload = {
      sessionId: this.sessionId,
      ...(this.config.userId ? { userId: this.config.userId } : {}),
      events: this.mapEventsToPayload(eventsToSend),
      devtools: devtoolsData
    };

    try {
      fetch(`${this.config.collectorUrl}/api/events`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch {
      // Synchronous throw is only possible on malformed URL/headers, which
      // can't happen at runtime. Network errors arrive asynchronously and
      // we can't react to them during unload anyway.
    }
  }

  /**
   * Stop recording
   */
  public stop(): void {
    if (this.stopRecording) {
      this.stopRecording();
      this.stopRecording = null;
    }

    // Explicit stop ends the tab session — clear the stored id so a later
    // re-init starts fresh rather than resuming this one. (Reloads never reach
    // here; they go through pagehide/beforeunload -> sendFinalFlush.)
    try { window.sessionStorage.removeItem(SessionRecorderImpl.SESSION_STORAGE_KEY); } catch { /* ignore */ }

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePagehide);

    // Flush remaining events and devtools data
    this.flushEvents();

    // Stop DevTools captures
    this.networkCaptor?.stop();
    this.consoleCaptor?.stop();
    this.performanceCaptor?.stop();
    this.errorCaptor?.stop();
    this.resourceErrorCaptor?.stop();
    this.frustrationCaptor?.stop();
    this.networkCaptor = null;
    this.consoleCaptor = null;
    this.performanceCaptor = null;
    this.errorCaptor = null;
    this.resourceErrorCaptor = null;
    this.frustrationCaptor = null;

    // Terminate flush worker
    this.flushWorker?.terminate();
    this.flushWorker = null;
    this.pendingFlushEvents.clear();

    this.initialized = false;
    this.log('info', 'SessionRecorder stopped');
  }

  /**
   * Discard the current session: tear down WITHOUT a final flush and ask the
   * collector to delete everything already stored for this session. Used by the
   * on-demand "cancel recording" control. Unlike stop() it drops the unsent
   * buffer and never flushes, so no late batch can 410 and spin up a new empty
   * session; the collector then deletes the already-uploaded events/devtools.
   */
  public discard(): void {
    const sessionId = this.sessionId;

    if (this.stopRecording) {
      this.stopRecording();
      this.stopRecording = null;
    }
    try { window.sessionStorage.removeItem(SessionRecorderImpl.SESSION_STORAGE_KEY); } catch { /* ignore */ }

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePagehide);

    this.networkCaptor?.stop();
    this.consoleCaptor?.stop();
    this.performanceCaptor?.stop();
    this.errorCaptor?.stop();
    this.resourceErrorCaptor?.stop();
    this.frustrationCaptor?.stop();
    this.networkCaptor = null;
    this.consoleCaptor = null;
    this.performanceCaptor = null;
    this.errorCaptor = null;
    this.resourceErrorCaptor = null;
    this.frustrationCaptor = null;

    // Drop the unsent buffer and tear down the worker WITHOUT flushing.
    this.events = [];
    this.clearDevToolsBuffers();
    this.flushWorker?.terminate();
    this.flushWorker = null;
    this.pendingFlushEvents.clear();

    this.initialized = false;

    // Delete whatever already reached the collector for this session.
    if (sessionId) {
      try {
        fetch(`${this.config.collectorUrl}/api/session/discard`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ sessionId }),
          keepalive: true
        }).catch(() => { /* best effort */ });
      } catch { /* ignore */ }
    }

    this.log('info', 'SessionRecorder discarded');
  }

  /**
   * Get current session info
   */
  public getSessionInfo(): SessionInfo {
    return {
      sessionId: this.sessionId || '',
      eventCount: this.events.length,
      isRecording: this.stopRecording !== null,
      startTime: this.startTime
    };
  }

  /**
   * Set the active user for the session. Safe to call multiple times — e.g. an
   * ERP where the operator switches DB user mid-session. Each DIFFERENT user is
   * recorded on the session's user timeline (server-side); repeated calls with
   * the same user are ignored so we don't spam /identify on every re-render.
   */
  public setUserId(userId: string): void {
    const uid = (userId || '').trim();
    if (!uid || uid === this.config.userId) return; // no-op on redundant same-user calls
    this.config.userId = uid;
    this.identify(uid);
  }

  private async identify(userId: string): Promise<void> {
    if (!this.sessionId || !userId) return;
    try {
      await this.sessionReady;
      await fetch(`${this.config.collectorUrl}/api/session/identify`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ sessionId: this.sessionId, userId })
      });
      this.log('info', 'Session identified with user');
    } catch (error) {
      this.log('error', 'Error identifying session:', error);
    }
  }

  /**
   * Manually capture an error
   */
  public captureError(error: Error): void {
    this.errorCaptor?.capture(error);
  }

  /**
   * Track Knockout.js ViewModels.
   *
   * @deprecated Disabled for performance reasons — subscribing to every
   * observable on a data-bound page caused noticeable UI slowdowns. This
   * method is now a no-op kept for backward compatibility.
   */
  public trackKnockout(_viewModel?: Record<string, unknown>, _name?: string, _properties?: string[]): void {
    this.log('warn', 'trackKnockout() is disabled. Knockout state tracking has been removed for performance reasons.');
  }

  /**
   * Emit a custom business event into the recorded stream. Lands as an
   * rrweb event with type=5 (Custom) carrying the supplied tag + payload,
   * so it appears at the correct timestamp on the replay timeline and in
   * the server-side Activity timeline / Assistant tools.
   *
   * Examples:
   *   SessionRecorder.addCustomEvent('order_submitted', { orderId: 'ORD-42' });
   *   SessionRecorder.addCustomEvent('feature_used', { name: 'export-pdf' });
   *   SessionRecorder.addCustomEvent('signup_completed');
   *   SessionRecorder.addCustomEvent('note_saved', 'draft @ 14:30', { color: '#facc15' });
   *
   * Optional third argument:
   *   color  – CSS color used to render this tag's tick marks on the player
   *            progress bar and the dot in the side panel. Overrides the
   *            automatic regex/hash pick. Per-TAG (the first event for a
   *            tag wins; subsequent calls with a different color for the
   *            SAME tag are ignored at display time). Stored alongside
   *            the payload so it travels through to the viewer; the
   *            original payload is preserved unchanged when read back.
   *
   * Constraints:
   *   - tag must be a non-empty string; reject silently with a warn if not
   *   - payload must be JSON-serializable; bad objects (circular refs,
   *     functions) are rejected so a stray business-event call can't
   *     crash the host page
   *   - Calling before init() is a no-op + warning. Events would have no
   *     recorder to attach to.
   */
  public addCustomEvent(tag: string, payload?: unknown, options?: { color?: string }): void {
    if (!this.initialized || this.stopRecording === null) {
      this.log('warn', `addCustomEvent('${tag}'): recorder not initialized, dropped.`);
      return;
    }
    if (typeof tag !== 'string' || tag.length === 0) {
      this.log('warn', 'addCustomEvent: tag must be a non-empty string, dropped.');
      return;
    }
    // Pre-serialize the payload so a bad object trips here (where we can
    // catch + log) rather than inside rrweb where it would silently break
    // event emission.
    let safePayload: unknown = payload;
    if (payload !== undefined) {
      try {
        const s = JSON.stringify(payload);
        safePayload = s === undefined ? null : JSON.parse(s);
      } catch (e) {
        this.log('warn', `addCustomEvent('${tag}'): payload not JSON-serializable, dropped. ${(e as Error)?.message ?? ''}`);
        return;
      }
    }

    // When color is supplied, wrap the payload in a Tracium envelope so it
    // travels with the event without polluting the user's payload shape.
    // The wrap is { _tracium: { color }, payload: <original> } — readers
    // detect this exact pair and unwrap before display. Original payload
    // is unchanged for downstream consumers that don't know about the wrap
    // (they still see the wrapped form — that's why the viewer/server
    // unwrappers exist).
    let wireValue: unknown = safePayload ?? null;
    if (options?.color && typeof options.color === 'string' && options.color.length > 0) {
      wireValue = { _tracium: { color: options.color }, payload: safePayload ?? null };
    }

    try {
      (record as unknown as { addCustomEvent: (tag: string, payload: unknown) => void })
        .addCustomEvent(tag, wireValue);
    } catch (e) {
      this.log('error', `addCustomEvent('${tag}') threw: ${(e as Error)?.message ?? e}`);
    }
  }

  /**
   * Check if recorder is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Build request headers including API key if configured
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }
    return headers;
  }

  /**
   * Log message based on debug setting
   */
  private log(level: 'info' | 'warn' | 'error', ...args: unknown[]): void {
    if (!this.config.debug && level === 'info') return;

    const prefix = '[SessionRecorder]';
    switch (level) {
      case 'info':
        console.log(prefix, ...args);
        break;
      case 'warn':
        console.warn(prefix, ...args);
        break;
      case 'error':
        console.error(prefix, ...args);
        break;
    }
  }
}

// Export singleton instance
export const SessionRecorder = new SessionRecorderImpl();
